import { useEffect, useState, useCallback } from "react";
import { getEventDetails } from "../services/eventService";

// Fetch event details using the identifier in the URL.
const useEventStatus = (eventCode) => {
  const [state, setState] = useState({ code: null, loading: true, data: null, error: null });
  const [revision, setRevision] = useState(0);
  const refetch = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    let current = true;
    setState({ code: eventCode, loading: true, data: null, error: null });
    async function fetchDetails() {
      try {
        if (!eventCode) throw new Error("Event code is required");
        const response = await getEventDetails(eventCode);
        const details = response?.data ?? response;
        if (response?.status === "ERROR" || !details?.id) {
          throw new Error(response?.message || "Unable to load event details.");
        }
        if (current) setState({ code: eventCode, loading: false, data: details, error: null });
      } catch (error) {
        if (current) setState({ code: eventCode, loading: false, data: null,
          error: error.response?.data?.message || error.message || "Unable to load event details." });
      }
    }
    fetchDetails();
    return () => { current = false; };
  }, [eventCode, revision]);

  const data = state.code === eventCode ? state.data : null;
  const normalizeStatus = (value) => String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
  const statuses = [data?.status, data?.event_status].map(normalizeStatus);
  // Lifecycle restrictions take precedence over a generic active/SUCCESS flag.
  const status = statuses.includes("completed") ? "completed"
    : statuses.some((value) => ["upcoming", "notstarted", "yettostart"].includes(value)) ? "upcoming"
    : statuses.find((value) => value && value !== "success") || "";
  const isEventExpired = data?.is_expired === true;
  return {
    data,
    loading: state.code !== eventCode || state.loading,
    error: state.code === eventCode ? state.error : null,
    status,
    isEventExpired,
    canUploadToEvent: Boolean(data) && !isEventExpired && data.is_active !== false && ["active", "ongoing"].includes(status),
    event_id: data?.id,
    refetch,
  };
};

export default useEventStatus;
